/**
 * ============================================================================
 * WELDING REPAIR ANALYSIS ENTRY MODULE
 * ============================================================================
 * 
 * PURPOSE:
 * Comprehensive form for creating, editing, and managing welding repair analysis records.
 * Handles the complete lifecycle from initiation to approval with multi-level workflow.
 * 
 * CORE FUNCTIONALITY:
 * 1. REPAIR RECORD CREATION: WE initiates repair analysis for specific project joints
 * 2. SUPERVISOR COMPLETION: Supervisor fills detailed repair analysis and root cause
 * 3. MULTI-LEVEL APPROVAL: WE → QCE → NDE approval workflow with closure remarks
 * 4. REVISION MANAGEMENT: Handles rejected repairs with revision tracking
 * 5. FILE MANAGEMENT: Supports document upload/download for repair evidence
 * 
 * DATA INTEGRATION SOURCES:
 * - getListofProjectno(): Fetches available projects from WI master data
 * - getWIweldingprocess(): Gets welding processes for selected project/joint from WI
 * - getAllInspectionAgency(): Loads inspection agencies for dropdown
 * - getClosureRepairWorkflow(): Retrieves approval workflow history
 * - getAllUsersByRole(): Validates approver availability before submission
 * 
 * WI INTEGRATION DETAILS:
 * - Project selection populates joint numbers from WI.JointNumbers.values
 * - Joint selection loads available welding processes from WI_Process
 * - Welder selection popup shows WI_Welder_Id and WI_Welder_Name
 * - System validates WI data integrity before allowing repair creation
 * 
 * ROLE-BASED WORKFLOW:
 * - WE (Welding Engineer): Initiates repair, provides basic project/joint info
 * - Supervisor: Completes detailed analysis, root cause, and repair specifics
 * - WE: Reviews and approves/rejects supervisor analysis
 * - QCE (Quality Control Engineer): Second level approval
 * - NDE (Non-Destructive Examination): Final approval
 * 
 * FORM SECTIONS:
 * 1. Basic Info: Report number, date, project, client, equipment details
 * 2. Repair Details: Joint, spot, revision, NDT method, material, welding process
 * 3. Repair Removal: Defect type, location marking, repair lengths
 * 4. After Removal: Repair method, findings, depth, defective process
 * 5. Root Cause Analysis: 5-Why analysis, corrective/preventive actions, cost
 * 6. Closure Remarks: Multi-level approval workflow with comments
 * 
 * KEY FEATURES:
 * - Dynamic field visibility based on user role and action (Add/Edit/View)
 * - Comprehensive validation with role-specific required fields
 * - File upload with drag-drop support and format validation
 * - Temp save functionality for incomplete forms
 * - Report number auto-generation (ProjectNumber/SequenceNumber)
 * - Revision tracking for rejected repairs
 * - Real-time welder selection from WI data
 * - Multi-select welding process dropdown
 * 
 * SUBMISSION LOGIC:
 * - Add: WE creates initial record → sends to Supervisor
 * - Edit (Supervisor): Completes analysis → sends to WE for approval
 * - Edit (WE/QCE/NDE): Provides approval/rejection → advances workflow
 * - Rejection: Creates new revision → returns to Supervisor
 * - Final Approval: Closes workflow → marks as "Approved"
 */
import { useState, useEffect, forwardRef, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Row, Col, Form, Button } from "react-bootstrap";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { BiSelectMultiple } from "react-icons/bi";
import dayjs from "dayjs";
import Delete from "../../icons/DeleteIcon.svg";
import MultiValuePopUp from "../utility/ui/modals/MultiValuePopUp";
import { showSuccess, showError, showWarning } from "../utility/ui/notifications/Toaster";
import { getListofProjectno, getWIweldingprocess } from "../../Services/WI-services";
import { getAllUsersByRole } from "../../Services/user-service";
import { addWeldingRepairAnalysis, getProjectReqCountProjectWise, getClosureRepairWorkflow, sortByPropertyInDescending, addWeldingRepairRoles } from "../../Services/weldingRepairAnalysis-service";
import { getAllInspectionAgency } from "../../Services/inspectionagency-service";
import { getCurrentDateTime, RenderIf } from "../../functions/welding-functions";
import { isExperience, isAlphanumericAllSpecialCharacters } from "../../functions/validations";
import ClosureRemarksWeldingRepair from "./ClosureRemarksWeldingRepair";
import Select from "react-select";
import { downloadAttachment, getPresignedURLWeldRepair } from "../../Services/Welder-services";
import axios from "axios";
import DeleteGrey from "../../icons/Delete_gray.svg";
import { emptyError } from "../WPSTransaction/WPSObjects/WPSCommonUtils";
import { NDT_Method, RepairFound, RepairLocationMarkedBy, RepairRemovedByAfterRepairRemoval } from "../WPSTransaction/WPSObjects/WPSGroove_entity";

const WeldingRepairAnalysisEntry = forwardRef((props, ref) => {
  const [currentrole, setCurrentrole] = useState("");
  const [jointNumbers, setJointNumbers] = useState([]);
  const [inspectionAgency, setInspectionAgency] = useState([]);
  const [weldingProcessResult, setWeldingProcessResult] = useState([]);
  const location = useLocation();
  const [showForm, setShowForm] = useState(false);
  const [closureWeldRepairObj, setClosureWeldRepairObj] = useState({});
  const [projectDetails, setProjectDetails] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [popUp, setPopUp] = useState({
    name: "",
    type: "",
    key: "",
    value: "",
  });

  const navigate = useNavigate();
  let userName = sessionStorage.getItem("UserName");
  let userId = sessionStorage.getItem("UserId")?.split("@")[0];
  let userRole = sessionStorage.getItem("Role");
  let todayDate = getCurrentDateTime("-");
  let action = location.state?.action;
  let SupervisorRole = process.env.REACT_APP_ROLE_SUPERVISOR;
  let rowjointnumber = location.state?.data.jointNumber;

  const [weldRepairObj, setWeldRepairObj] = useState(
    location.state?.action === "Edit" || location.state?.action === "View"
      ? location.state?.data
      : {
        PK: "weldRepairAnalysis#year#",
        SK: "",
        reportNumber: "",
        initiatedDate: "",
        projectNumber: "",
        client: "",
        equipmentNumber: "",
        inspectionBy: "",
        drawingNumber: "",
        sheetNumber: "",
        revisionNumber: "",
        wld_file: "",
        orginatingDept: "Manufacturing",
        jointNumber: "",
        spotNumber: "",
        repairRevision: "",
        ndtMethod: "",
        material: "",
        weldingProcess: "",
        welderNumber: "",
        welderName: "",
        welderArrData: [],
        repairOrDefectType: "",
        repairLocationMarkedBy: "",
        repairLengthSpotwise: "",
        repairLengthActualwise: "",
        repairRemovedByAfterRepairRemoval: "",
        repairFoundAfterRepairRemoval: "",
        repairDepthAfterRepairRemoval: "",
        defectiveProcessAfterRepairRemoval: "",
        weldingJointDetailAfterRepairRemoval: "",
        rootCauseAnalysisWhy1: "",
        rootCauseAnalysisWhy2: "",
        rootCauseAnalysisWhy3: "",
        rootCauseAnalysisWhy4: "",
        rootCauseAnalysisWhy5: "",
        rootCauseAnalysisOthers: "",
        rootCauseAnalysisRootCause: "",
        rootCauseAnalysisCorrectiveAction: "",
        rootCauseAnalysisPreventionAction: "",
        rootCauseAnalysisKaizenIdea: "",
        rootCauseAnalysisCounterMeasure: "",
        rootCauseAnalysisCostOfRepair: "",
      }
  );

  const [error, setError] = useState({
    projectNumber: {},
    client: {},
    equipmentNumber: {},
    reportNumber: {},
    inspectionBy: {},
    drawingNumber: {},
    sheetNumber: {},
    revisionNumber: {},
    jointNumber: {},
    spotNumber: {},
    repairRevision: {},
    ndtMethod: {},
    material: {},
    welderNumber: {},
    weldingProcess: {},
    repairOrDefectType: {},
    repairLocationMarkedBy: {},
    repairLengthSpotwise: {},
    repairLengthActualwise: {},
    repairRemovedByAfterRepairRemoval: {},
    repairFoundAfterRepairRemoval: {},
    defectiveProcessAfterRepairRemoval: {},
    repairDepthAfterRepairRemoval: {},
    weldingJointDetailAfterRepairRemoval: {},
    rootCauseAnalysisWhy1: {},
    rootCauseAnalysisWhy2: {},
    rootCauseAnalysisWhy3: {},
    rootCauseAnalysisWhy4: {},
    rootCauseAnalysisWhy5: {},
    rootCauseAnalysisOthers: {},
    rootCauseAnalysisRootCause: {},
    rootCauseAnalysisCorrectiveAction: {},
    rootCauseAnalysisPreventionAction: {},
    rootCauseAnalysisKaizenIdea: {},
    rootCauseAnalysisCounterMeasure: {},
    rootCauseAnalysisCostOfRepair: {},
    repairReqApprover1Comment: {},
    repairReqApprover1Status: {},
    repairReqApprover2Comment: {},
    repairReqApprover2Status: {},
    repairReqApprover3Comment: {},
    repairReqApprover3Status: {},
  });

  let folderName = "weld_repair_analysis_files";

  const handleOndragOver = (event) => {
    event.preventDefault();
  };
  const handleOndrop = (event) => {
    let imageFile = event.dataTransfer.files[0];
    handleFile(imageFile);
  };

  const [file, setFile] = useState("");
  const fileInputRef = useRef(null);

  const handleFile = (file) => {
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      showWarning("File size exceeds the allowed limit. Add File Less Than 5 Mb");
      file = null;
      return;
    }

    let uploadextension = file.name.split(".");
    if (
      uploadextension[1] !== "docs" &&
      uploadextension[1] !== "xlsx" &&
      uploadextension[1] !== "ppt" &&
      uploadextension[1] !== "zip" &&
      uploadextension[1] !== "jpeg" &&
      uploadextension[1] !== "png" &&
      uploadextension[1] !== "pdf" &&
      uploadextension[1] !== "csv" &&
      uploadextension[1] !== "jpg"
    ) {
      alert(
        "1.Please Upload docx, xlsx, ppt, jpeg, jpg, png, pdf, zip files. 2.please rename the file without special characters + .(extension)"
      );
      file = null;
      return;
    }

    setFile(file);
    let ImageName = file.name.replace(/ /g, "_");
    let finalname = ImageName;
    weldRepairObj.wld_file = finalname;
  };

  const fetchListofProject = async () => {
    const result = await getListofProjectno();

    setProjectDetails(result);
  };

  const fetchInspectionAgency = async () => {
    const result = await getAllInspectionAgency();
    setInspectionAgency(result);
  };

  const fetchWIWeldingProcesses = async () => {
    const result = await getWIweldingprocess(
      weldRepairObj.projectNumber,
      weldRepairObj.jointNumber
    );
    if (result?.length > 0) {
      let temp = [];
      for (let i of result) {
        let chk = temp.filter((x) => x.value === i.WI_Process);
        if (chk.length === 0) {
          temp.push({ value: i.WI_Process, label: i.WI_Process });
        }
      }
      setWeldingProcessResult(temp);
    }
  };

  const fetchClosureData = async () => {
    if (location.state?.data?.reportNumber !== "") {
      let tempObj1 = {
        fieldValue: location.state?.data?.reportNumber,
        flag: "All",
      };
      let closureresult = await getClosureRepairWorkflow(tempObj1);

      setClosureWeldRepairObj(closureresult[0]);
    }
  };

  const showPopUp = (name, type, key, value) => {
    if (weldRepairObj.weldingProcess === "") {
      error.weldingProcess = {
        status: true,
        message: "Kindly select Welding Process first.",
      };

      setError((prevState) => ({
        ...prevState,
        weldingProcess: error,
      }));
    } else {
      setError((prevState) => ({
        ...prevState,
        projectNumber: {},
      }));

      setPopUp({ name, type, key, value });
      setShowForm(true);
    }
  };

  const setPopUpData = (data) => {
    if (data.name === "Welding Repair- WI Welding Numbers") {
      let temp = [];
      let temp1 = [];
      for (let item of data.result) {
        temp.push(item.WI_Welder_Id.trim());
        temp1.push(item.WI_Welder_Name.trim());
      }
      setWeldRepairObj((prevState) => ({
        ...prevState,
        welderNumber: String(temp),
        welderName: String(temp1),
      }));
    }
  };

  useEffect(() => {
    if (userRole === "WE") {
      setCurrentrole("Initiator");
    }
    if (userRole === SupervisorRole) {
      setCurrentrole(SupervisorRole);
    }


    fetchInspectionAgency();
    fetchListofProject();


    if (action === "Edit") {
      if (SupervisorRole === "Supervisor") {
        fetchWIWeldingProcesses();
      } else {
        let welprocess = location.state?.data.weldingProcess.split(",");

        let temp = [];
        if (welprocess?.length > 0) {
          for (let i of welprocess) {
            temp.push({ value: i, label: i });
          }
        }
        setSelectedOptions(temp);
      }
      fetchClosureData();
    }
  }, []);

  const handleDropDownChange = (event) => {
    const { name, value } = event.target;
    if (name === "projectNumber") {
      setWeldRepairObj((prevState) => ({
        ...prevState,
        projectNumber: value,
      }));

      const selectedProjects = projectDetails.filter(
        (item) => item.SK === value
      );

      const uniqueJointNos = selectedProjects.map(
        (project, id) => project.JointNumbers.values
      );

      let abc = uniqueJointNos[0];

      setJointNumbers(abc);
    } else {
      setWeldRepairObj((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    }
  };

  const handleChange = async (e) => {
    const { name, value } = e.target;
    setWeldRepairObj((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleDateChange = (value, name) => {
    //
    let yyyy = value.getFullYear();
    let mm = value.getMonth() + 1; // Months start at 0!
    setWeldRepairObj((prevState) => ({
      ...prevState,
      Date: value,
      CreatedDate: value,
      Month: mm,
      Year: yyyy,
    }));
  };

  const validateRepairAnalysis = () => {
    let isError = false;

    let temperror = emptyError(error, setError, isError);

    if (action === "Add") {
      if (weldRepairObj.projectNumber === "") {
        weldRepairObj.projectNumber = "";
        isError = true;
        error.projectNumber = {
          status: true,
          message: "Project Number is required.",
        };
      }
      if (weldRepairObj.jointNumber === "") {
        weldRepairObj.jointNumber = "";
        isError = true;
        error.jointNumber = {
          status: true,
          message: "Joint Number is required.",
        };
      }
    }

    if (action === "Edit" && userRole === SupervisorRole) {
      if (weldRepairObj.repairOrDefectType === "") {
        weldRepairObj.repairOrDefectType = "";
        isError = true;
        error.repairOrDefectType = {
          status: true,
          message: "Repair defect type is required.",
        };
      } else {
        error.repairOrDefectType = {
          status: false,
          message: "",
        };
      }

      if (weldRepairObj.inspectionBy === "") {
        weldRepairObj.inspectionBy = "";
        isError = true;
        error.inspectionBy = {
          status: true,
          message: "Inspection By is required.",
        };
      } else {
        error.inspectionBy = {
          status: false,
          message: "",
        };
      }

      if (weldRepairObj.spotNumber === "") {
        weldRepairObj.spotNumber = "";
        isError = true;
        error.spotNumber = {
          status: true,
          message: "spotNumber  is required.",
        };
      } else {
        error.spotNumber = {
          status: false,
          message: "",
        };
      }

      if (weldRepairObj.repairRevision === "") {
        weldRepairObj.repairRevision = "";
        isError = true;
        error.repairRevision = {
          status: true,
          message: "repairRevision is required.",
        };
      } else {
        error.repairRevision = {
          status: false,
          message: "",
        };
      }

      if (weldRepairObj.ndtMethod === "") {
        weldRepairObj.ndtMethod = "";
        isError = true;
        error.ndtMethod = {
          status: true,
          message: "ndtMethod  is required.",
        };
      } else {
        error.ndtMethod = {
          status: false,
          message: "",
        };
      }

      if (weldRepairObj.material === "") {
        weldRepairObj.material = "";
        isError = true;
        error.material = {
          status: true,
          message: "material  is required.",
        };
      } else {
        error.material = {
          status: false,
          message: "",
        };
      }

      if (weldRepairObj.weldingProcess === "") {
        weldRepairObj.weldingProcess = "";
        isError = true;
        error.weldingProcess = {
          status: true,
          message: "welding Process  is required.",
        };
      } else {
        error.weldingProcess = {
          status: false,
          message: "",
        };
      }

      if (weldRepairObj.welderNumber === "") {
        weldRepairObj.welderNumber = "";
        isError = true;
        error.welderNumber = {
          status: true,
          message: "welding Number  is required.",
        };
      } else {
        error.welderNumber = {
          status: false,
          message: "",
        };
      }

      if (weldRepairObj.repairLocationMarkedBy === "") {
        weldRepairObj.repairLocationMarkedBy = "";
        isError = true;
        error.repairLocationMarkedBy = {
          status: true,
          message: "Repair location marked by is required.",
        };
      } else {
        error.repairLocationMarkedBy = {
          status: false,
          message: "",
        };
      }

      if (weldRepairObj.repairLengthSpotwise === "") {
        weldRepairObj.repairLengthSpotwise = "";
        isError = true;
        error.repairLengthSpotwise = {
          status: true,
          message: "Repair length spot wise is required.",
        };
      } else {
        error.repairLengthSpotwise = {
          status: false,
          message: "",
        };
      }
      if (weldRepairObj.repairLengthActualwise === "") {
        weldRepairObj.repairLengthActualwise = "";
        isError = true;
        error.repairLengthActualwise = {
          status: true,
          message: "Repair length actual wise is required.",
        };
      } else {
        error.repairLengthActualwise = {
          status: false,
          message: "",
        };
      }
      if (weldRepairObj.defectiveProcessAfterRepairRemoval === "") {
        weldRepairObj.defectiveProcessAfterRepairRemoval = "";
        isError = true;
        error.defectiveProcessAfterRepairRemoval = {
          status: true,
          message: "Defective process is required.",
        };
      } else {
        error.defectiveProcessAfterRepairRemoval = {
          status: false,
          message: "",
        };
      }
      if (weldRepairObj.weldingJointDetailAfterRepairRemoval === "") {
        weldRepairObj.weldingJointDetailAfterRepairRemoval = "";
        isError = true;
        error.weldingJointDetailAfterRepairRemoval = {
          status: true,
          message: "Welding Joint details is required.",
        };
      } else {
        error.weldingJointDetailAfterRepairRemoval = {
          status: false,
          message: "",
        };
      }

      if (weldRepairObj.repairRemovedByAfterRepairRemoval === "") {
        weldRepairObj.repairRemovedByAfterRepairRemoval = "";
        isError = true;
        error.repairRemovedByAfterRepairRemoval = {
          status: true,
          message: "Welding Joint details is required.",
        };
      } else {
        error.repairRemovedByAfterRepairRemoval = {
          status: false,
          message: "",
        };
      }

      if (weldRepairObj.repairFoundAfterRepairRemoval === "") {
        weldRepairObj.repairFoundAfterRepairRemoval = "";
        isError = true;
        error.repairFoundAfterRepairRemoval = {
          status: true,
          message: "Welding Joint details is required.",
        };
      } else {
        error.repairFoundAfterRepairRemoval = {
          status: false,
          message: "",
        };
      }

      if (weldRepairObj.repairDepthAfterRepairRemoval === "") {
        weldRepairObj.repairDepthAfterRepairRemoval = "";
        isError = true;
        error.repairDepthAfterRepairRemoval = {
          status: true,
          message: "Welding Joint details is required.",
        };
      } else {
        error.repairDepthAfterRepairRemoval = {
          status: false,
          message: "",
        };
      }

      if (weldRepairObj.rootCauseAnalysisWhy1 === "") {
        weldRepairObj.rootCauseAnalysisWhy1 = "";
        isError = true;
        error.rootCauseAnalysisWhy1 = {
          status: true,
          message: "Root cause analysis is required.",
        };
      } else {
        error.rootCauseAnalysisWhy1 = {
          status: false,
          message: "",
        };
      }
    } else if (action === "Edit" && userRole === "WE") {
      if (closureWeldRepairObj.repairReqApprover1Comment === "") {
        closureWeldRepairObj.repairReqApprover1Comment = "";
        isError = true;
        error.repairReqApprover1Comment = {
          status: true,
          message: "Comment is required.",
        };
      } else {
        error.repairReqApprover1Comment = {
          status: false,
          message: "",
        };
      }
      if (closureWeldRepairObj.repairReqApprover1Status === "") {
        closureWeldRepairObj.repairReqApprover1Status = "";
        isError = true;
        error.repairReqApprover1Status = {
          status: true,
          message: "Status is required.",
        };
      } else {
        error.repairReqApprover1Status = {
          status: false,
          message: "",
        };
      }
    } else if (action === "Edit" && userRole === "QCE") {
      if (closureWeldRepairObj.repairReqApprover2Comment === "") {
        closureWeldRepairObj.repairReqApprover2Comment = "";
        isError = true;
        error.repairReqApprover2Comment = {
          status: true,
          message: "Comment is required.",
        };
      } else {
        error.repairReqApprover2Comment = {
          status: false,
          message: "",
        };
      }
      if (closureWeldRepairObj.repairReqApprover2Status === "") {
        closureWeldRepairObj.repairReqApprover2Status = "";
        isError = true;
        error.repairReqApprover2Status = {
          status: true,
          message: "Status is required.",
        };
      } else {
        error.repairReqApprover2Status = {
          status: false,
          message: "",
        };
      }
    } else if (action === "Edit" && userRole === "NDE") {
      if (closureWeldRepairObj.repairReqApprover3Comment === "") {
        isError = true;
        error.repairReqApprover3Comment = {
          status: true,
          message: "Comment is required.",
        };
      } else {
        error.repairReqApprover3Comment = {
          status: false,
          message: "",
        };
      }
      if (closureWeldRepairObj.repairReqApprover3Status === "") {
        isError = true;
        error.repairReqApprover3Status = {
          status: true,
          message: "Status is required.",
        };
      } else {
        error.repairReqApprover3Status = {
          status: false,
          message: "",
        };
      }
    }
    setError((prevState) => ({
      ...prevState,
      ...error,
    }));

    return { isError, temperror };
  };

  const validateRepairAnalysisTemp = () => {
    let isError = false;

    let temperror = emptyError(error, setError, isError);

    return { isError, temperror };
  };

  const handlecancel = () => {
    navigate("/weldingRepair/WeldingRepairAnalysis", {
      state: { data: "", action: "Add" },
    });
  };

  // Helper function for success handling and navigation
  function handleSuccessAndNavigate(message) {
    showSuccess(message);
    setTimeout(() => {
      navigate("/weldingRepair/WeldingRepairAnalysis", {
        state: { data: "", action: "Add" },
      });
    }, 2000);
  }

  // Helper function for role workflow processing
  async function processRoleWorkflow(role, approverStatus) {
    const isRejected = approverStatus === "Rejected";

    weldRepairObj.updatedBy = userId;
    weldRepairObj.updatedByName = userName;

    if (isRejected) {
      weldRepairObj.repairAnalysisStatus = "Rejected";
      weldRepairObj.approvalPendingWith = SupervisorRole;
    } else {
      if (role === "NDE") {
        weldRepairObj.repairAnalysisStatus = "Approved";
        weldRepairObj.approvalPendingWith = "Closed";
      } else {
        weldRepairObj.repairAnalysisStatus = "In Progress";
        if (role === "WE") {
          weldRepairObj.approvalPendingWith = "QCE";
        } else {
          weldRepairObj.approvalPendingWith = "NDE";
        }
      }
    }

    const result = await addWeldingRepairAnalysis(weldRepairObj, action);
    if (result?.status) {
      action = "";
      closureWeldRepairObj.approvalPendingWith =
        weldRepairObj.approvalPendingWith;
      await addWeldingRepairAnalysis(closureWeldRepairObj, action);
      handleSuccessAndNavigate(result.message);
    }
  }

  const handleTempSave = async (e) => {
    const allFieldsEmpty = Object.entries(weldRepairObj).every(
      ([key, value]) => {
        return (
          !key.match(
            /^(client|equipment|inspection|drawing|sheet|revision|spot|repair|ndt|material|welding|welder|defective|rootCauseAnalysis)/
          ) || value === ""
        );
      }
    );

    if (allFieldsEmpty) {
      showError("Atleast fill any one field to temp save.");
      return;
    }

    weldRepairObj.tempFlag = true;
    weldRepairObj.approvalPendingWith = SupervisorRole;
    let val = validateRepairAnalysisTemp();
    let result;
    if (val.temperror === false) {
      result = await addWeldingRepairAnalysis(weldRepairObj, action);
    } else {
      showWarning("Special Characters # & % + - ' < > = ; not allowed");
    }

    if (result?.status) {
      showWarning("Weld Repair is Temp saved");
      setTimeout(async () => {
        navigate("/weldingRepair/WeldingRepairAnalysis", {
          state: { data: "", action: "Add" },
        });
      }, 2000);
    }
  }

  const handleRoleError = async () => {
    const roleConfig = {
      [SupervisorRole]: {
        roles: [{ role: process.env.REACT_APP_ROLE_SUPERVISOR }],
        msg: "No welding engineer maintained in User Master. Cannot submit for approval.",
      },
      WE: {
        roles: [{ role: process.env.REACT_APP_ROLE_QCENGINEER }],
        msg: "No QC engineer maintained in User Master. Cannot submit for approval.",
        condition: action !== "Add",
      },
      QCE: {
        roles: [{ role: process.env.REACT_APP_ROLE_NDENGINEER }],
        msg: "No NDE engineer maintained in User Master. Cannot submit for approval.",
      },
      NDE: {
        roles: [{ role: process.env.REACT_APP_ROLE_SUPERVISOR }],
        msg: "No supervisor engineer maintained in User Master. Cannot submit for rejection.",
      },
    };

    const config = roleConfig[userRole];
    if (config) {
      const { roles, msg, condition } = config;
      if (!condition || condition === true) {
        const usermasterresult = await getAllUsersByRole(roles);

        if (
          (usermasterresult?.length === 0 &&
            action !== "Add" &&
            userRole !== "NDE") ||
          (usermasterresult?.length === 0 &&
            closureWeldRepairObj.repairReqApprover3Status === "Rejected" &&
            userRole === "NDE")
        ) {
          showError(msg);
          return false;
        }
      }
    }
    return true

  }
  const handleSubmit = async (e) => {
    e.preventDefault();
    //
    if (e.nativeEvent.submitter.name === "tempsave") {
      await handleTempSave()
    } else {
      // Define role requirements based on user role
      if (!handleRoleError()) {
        return
      }

      let val = validateRepairAnalysis();
      if (val.isError || val.temperror) {
        showWarning(
          val.temperror
            ? "Special Characters # & % + - ' < > = ; not allowed"
            : "All Fields are Required"
        );
        return;
      }

      if (action === "Add") {
        const now = getCurrentDateTime("-");
        const newDate = new Date();
        const month = newDate.getMonth() + 1;
        const year = newDate.getFullYear();

        // Set common properties
        Object.assign(weldRepairObj, {
          createdOn: now,
          createdBy: userId,
          updatedBy: userId,
          updatedByName: userName,
          createdByName: userName,
          updatedOn: now,
          initiatedDate: now,
          month,
          year,
          repairAnalysisStatus: "In Progress",
          approvalPendingWith: SupervisorRole,
          workflowRevisionNumber: 0,
        });
        //first Time Add
        if (userRole === "WE") {
          let maxArr = await getProjectReqCountProjectWise({
            year: year,
            projectNumber: weldRepairObj.projectNumber,
          });

          let maxSeqNo = 0;
          if (maxArr.length === 0) {
            maxSeqNo = 1;
          } else {
            maxSeqNo = maxArr[maxArr.length - 1].RaisedCount;
          }

          weldRepairObj.SeqNo = maxSeqNo;
        }
        weldRepairObj.reportNumber = weldRepairObj.projectNumber + "/" + weldRepairObj.SeqNo;
        weldRepairObj.approvalPendingWith = SupervisorRole;
        weldRepairObj.workflowRevisionNumber = 0;
        weldRepairObj.PK = "weldRepairAnalysis#year#" + weldRepairObj.year;
        weldRepairObj.SK =
          "ProjectNumber#" +
          weldRepairObj.projectNumber +
          "#RaisedBy#" +
          userId +
          "#SeqNo#" +
          weldRepairObj.SeqNo;
        let result = await addWeldingRepairAnalysis(weldRepairObj, action);
        let roles = [{ role: SupervisorRole, desc: "supervisorEngineer" }, { role: "WE", desc: "Welding Engineer" }, { role: "NDE", desc: "NDE" }, { role: "QCE", desc: "Quality Engineer" }]
        let users = await getAllUsersByRole(roles)

        await Promise.all(roles.map(async (role) => {
          let user = users.filter(item => item.um_role == role)
          let emailid = []
          user.map((email) => {
            emailid.push(email.SK)
          })
          let obj = { PK: role.role, SK: role.desc, weldingEnggId: emailid }
          await addWeldingRepairRoles(obj)
        }));

        if (result?.status) {
          handleSuccessAndNavigate(result.message);
        }
      } else if (
        action === "Edit" &&
        weldRepairObj.approvalPendingWith === SupervisorRole
      ) {
        let result;
        weldRepairObj.updatedBy = userId;
        weldRepairObj.updatedByName = userName;
        weldRepairObj.updatedOn = getCurrentDateTime("-");

        if (weldRepairObj.repairAnalysisStatus === "Rejected") {
          let temprev = 0;
          let tmpObj = {
            fieldValue: weldRepairObj.reportNumber,
            flag: "All",
          };
          let closureRevisionNumberResult = await getClosureRepairWorkflow(
            tmpObj
          );
          let maxRevisionNo = 0;
          if (closureRevisionNumberResult?.length > 0) {
            let sortedArr = closureRevisionNumberResult.sort(
              sortByPropertyInDescending("revisionNumber")
            );
            maxRevisionNo = sortedArr[0].revisionNumber;
          }
          temprev = maxRevisionNo + 1;
          weldRepairObj.workflowRevisionNumber = temprev;
        }
        weldRepairObj.repairAnalysisStatus = "In Progress";
        weldRepairObj.approvalPendingWith = "WE";
        weldRepairObj.tempFlag = false;
        result = await addWeldingRepairAnalysis(weldRepairObj, action);

        if (result?.status) {
          let tempObj = {
            PK: "reportNumber#" + weldRepairObj.reportNumber,
            SK: "revisionNumber#" + weldRepairObj.workflowRevisionNumber,
            projectNumber: weldRepairObj.projectNumber,
            revisionNumber: weldRepairObj.workflowRevisionNumber,
            reportNumber: weldRepairObj.reportNumber,
            approvalPendingWith: weldRepairObj.approvalPendingWith,
            repairSupervisorName: userName,
            repairSupervisorEmailId: userId,
            repairSupervisorCreatedOn: todayDate,
            repairReqApprover1: "WE",
            repairReqApprover1Comment: "",
            repairReqApprover1Status: "",
            repairReqApprover1CreatedOn: "",
            repairReqApprover2: "QCE",
            repairReqApprover2Comment: "",
            repairReqApprover2Status: "",
            repairReqApprover2CreatedOn: "",
            repairReqApprover3: "NDE",
            repairReqApprover3Comment: "",
            repairReqApprover3Status: "",
            repairReqApprover3CreatedOn: "",
          };
          setClosureWeldRepairObj(tempObj);
          await addWeldingRepairAnalysis(tempObj, action);
          handleSuccessAndNavigate(result.message);
        }
      } else if (
        userRole === "WE" &&
        weldRepairObj.approvalPendingWith === "WE"
      ) {
        processRoleWorkflow(
          "WE",
          closureWeldRepairObj.repairReqApprover1Status
        );
      } else if (
        userRole === "QCE" &&
        weldRepairObj.approvalPendingWith === "QCE"
      ) {
        processRoleWorkflow(
          "QCE",
          closureWeldRepairObj.repairReqApprover2Status
        );
      } else if (
        userRole === "NDE" &&
        weldRepairObj.approvalPendingWith === "NDE"
      ) {
        processRoleWorkflow(
          "NDE",
          closureWeldRepairObj.repairReqApprover3Status
        );
      }
    }

    if (weldRepairObj.wld_file !== "" && file !== "") {
      const result = await getPresignedURLWeldRepair(
        folderName,
        weldRepairObj.wld_file
      );

      const posturl = result.url;
      await axios.put(posturl, file);
    }
  };

  const callReload = () => {
    props.onHide();
    props.setMainReload(true);
  };

  const getRemarks = (e, currentClosureObj, currentWeldObj, levelname) => {
    if (e.target.name.includes("Comment")) {
      currentClosureObj["repairReqApprover" + levelname] = userName;
      currentClosureObj["repairReqApprover" + levelname + "Comment"] =
        e.target.value;
      currentClosureObj["repairReqApprover" + levelname + "CreatedOn"] =
        todayDate;
      currentWeldObj.updatedBy = userId;
      currentWeldObj.updatedByName = userName;
      currentWeldObj.updatedOn = todayDate;
    }
    if (e.target.name.includes("Status")) {
      currentClosureObj["repairReqApprover" + levelname + "Status"] =
        e.target.value;
    }
    setClosureWeldRepairObj(currentClosureObj);
  };

  const handleDropDownProcess = (e) => {
    let temp = [];
    let temp1 = [];
    if (weldRepairObj.weldingProcess !== "") {
      temp1.push(weldRepairObj.weldingProcess);
      for (let dt of e) {
        if (!weldRepairObj.weldingProcess.includes(dt.value)) {
          temp1.push(dt.value);
        }
        temp.push({ value: dt.value, label: dt.value });
      }
      setSelectedOptions(temp);
    } else {
      temp.push({ value: e[0].value, label: e[0].value });
      setSelectedOptions(temp);
      temp1.push(e[0].value);
    }
    setWeldRepairObj((prevState) => ({
      ...prevState,
      weldingProcess: String(temp1),
      defectiveProcessAfterRepairRemoval: String(temp1),
    }));
  };

  const handleDeleteDocEvent = async (fileName) => {
    setWeldRepairObj((prevState) => ({
      ...prevState,
      wld_file: "",
    }));
  };

  const handleDownloadDocument = async (fileName) => {
    downloadAttachment({
      foldername: "weld_repair_analysis_files",
      // subfoldername: weldRepairObj.wld_file,
      filename: fileName,
    }).catch((err) => {
      showError(`Error in fetching file.`);
    });
  };

  const getSelectValue = () => {
    if (action === "View") {
      return weldRepairObj.weldingProcess;
    }
    return selectedOptions;
  };

  return (
    <>
      <div className="position-absolute ms-3" style={{ top: "15px" }}>
        <strong>Enter Weld Repair</strong>
      </div>

      <Form onSubmit={handleSubmit} autoComplete="off">
        <div
          className="mt-2"
          style={{
            height: window.innerHeight - 160,
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          <div className="weldRepairFirstDiv">
            <div className="weldRepairSecondDiv">
              <Row
                style={{
                  position: "relative",
                  alignItems: "center",
                }}
              >
                <Col lg={3} className="mb-2">
                  <Form.Group className="g-input">
                    <div className="d-grid align-items-center">
                      <Form.Control
                        id="ReportNumber"
                        name="ReportNumber"
                        type="text"
                        maxLength="50"
                        readOnly
                        placeholder=" "
                        value={weldRepairObj.reportNumber}
                        onChange={handleChange}
                      />
                      <Form.Label htmlFor="reportNumber">
                        Report Number
                      </Form.Label>
                    </div>
                  </Form.Group>
                </Col>
                <Col lg={3}>
                  <div className="groove_date">
                    <div className="Date-input w-100">
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DemoContainer components={["DatePicker"]}>
                          <DatePicker
                            label="Date"
                            className="groove_datecolor"
                            value={
                              weldRepairObj.initiatedDate
                                ? dayjs(weldRepairObj.initiatedDate)
                                : dayjs(todayDate)
                            }
                            disabled={true}
                            onChange={(newValue) => {
                              let date = dayjs(newValue).format("YYYY-MM-DD");
                              handleDateChange(date, "lastUpdatedOn");
                            }}
                            format="DD-MM-YYYY"
                          />
                        </DemoContainer>
                      </LocalizationProvider>
                    </div>
                  </div>
                </Col>
                <Col lg={6}>
                  <Form.Group>
                    <form encType="multipart/form-data">
                      {weldRepairObj.wld_file === "" &&
                        currentrole === SupervisorRole ? (
                        <div className="userpic_upload">
                          <div
                            className="drop_zone"
                            onDragOver={handleOndragOver}
                            onDrop={handleOndrop}
                          >
                            <Form.Label
                              className="userpic_text_black"
                              htmlFor="fileInput"
                            >
                              Upload image here or
                            </Form.Label>{" "}
                            <Form.Label
                              className="userpic_text_blue"
                              htmlFor="fileInput"
                            >
                              Select a file
                            </Form.Label>
                            <br></br>
                            <Form.Label className="pic_text1">
                              Supports: .docx, .xlsx, .ppt, .jpeg, .jpg, .png,
                              .zip, Size:Max 5MB
                            </Form.Label>
                            <br></br>
                            <Form.Label className="imgtext">
                              {weldRepairObj.wld_file}
                            </Form.Label>
                            <input
                              id="fileInput"
                              name="fileInput"
                              type="file"
                              accept="*"
                              hidden
                              ref={fileInputRef}
                              onChange={(e) => handleFile(e.target.files[0])}
                            />
                          </div>
                        </div>
                      ) : (
                        <>
                          <Form.Label className="fileuploadTitle">
                            Upload Image
                          </Form.Label>
                          <Col lg={12} md={12} xs={12}>
                            <Form.Label
                              className="add-new-link"
                              onClick={() =>
                                handleDownloadDocument(weldRepairObj.wld_file)
                              }
                            >
                              {weldRepairObj.wld_file === ""
                                ? "No file chosen"
                                : weldRepairObj.wld_file}
                              &nbsp;&nbsp;&nbsp;
                            </Form.Label>

                            <img
                              onMouseOver={(e) =>
                                (e.currentTarget.src = Delete)
                              }
                              onMouseOut={(e) =>
                                (e.currentTarget.src = DeleteGrey)
                              }
                              src={DeleteGrey}
                              alt="Delete"
                              onClick={() =>
                                handleDeleteDocEvent(weldRepairObj.wld_file)
                              }
                            />
                          </Col>
                        </>
                      )}
                    </form>
                    &nbsp;&nbsp;
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col lg={3} className="mb-2">
                  <Form.Group className="g-select">
                    <div className="d-grid align-items-center">
                      <Form.Select
                        id="projectNumber"
                        name="projectNumber"
                        type="text"
                        placeholder=" "
                        value={weldRepairObj.projectNumber}
                        disabled={action === "Edit"}
                        onChange={(e) => {
                          handleDropDownChange(e);
                        }}
                      >
                        <option value="">Select</option>
                        {projectDetails
                          ? projectDetails.map((projnum) => (
                            <option key={projnum} value={projnum.SK}>
                              {projnum.SK}
                            </option>
                          ))
                          : null}
                      </Form.Select>
                      <Form.Label htmlFor="projectNumber">
                        Project Number
                      </Form.Label>
                    </div>
                    <div className="required-text-selectWeldRepair">
                      {error.projectNumber.status && (
                        <Form.Text className="text-danger">
                          {error.projectNumber.message}
                        </Form.Text>
                      )}
                    </div>
                  </Form.Group>
                </Col>

                <Col lg={3}>
                  <Form.Group className="g-input">
                    <div className="d-grid align-items-center">
                      <Form.Control
                        id="client"
                        name="client"
                        type="text"
                        maxLength="50"
                        placeholder=" "
                        value={weldRepairObj.client}
                        readOnly={currentrole !== SupervisorRole}
                        onChange={handleChange}
                        onBlur={(e) => {
                          let error = isAlphanumericAllSpecialCharacters(
                            e.target.value
                          );
                          setError((prevState) => ({
                            ...prevState,
                            client: error,
                          }));
                        }}
                      />
                      <Form.Label htmlFor="client">Client</Form.Label>
                      {error.client.status && (
                        <Form.Text className="text-danger">
                          {error.client.message}
                        </Form.Text>
                      )}
                    </div>
                  </Form.Group>
                </Col>
                <Col lg={3}>
                  <Form.Group className="g-input">
                    <div className="d-grid align-items-center">
                      <Form.Control
                        id="equipmentNumber"
                        name="equipmentNumber"
                        type="text"
                        maxLength="50"
                        placeholder=" "
                        value={weldRepairObj.equipmentNumber}
                        readOnly={currentrole !== SupervisorRole}
                        onChange={handleChange}
                      />
                      <Form.Label htmlFor="equipmentNumber">
                        Equipment No.
                      </Form.Label>
                      {error.equipmentNumber.status && (
                        <Form.Text className="text-danger">
                          {error.equipmentNumber.message}
                        </Form.Text>
                      )}
                    </div>
                  </Form.Group>
                </Col>
                <Col lg={3}>
                  <Form.Group className="g-select">
                    <div className="d-grid align-items-center">
                      <Form.Select
                        id="inspectionBy"
                        name="inspectionBy"
                        type="text"
                        value={weldRepairObj.inspectionBy}
                        disabled={currentrole !== SupervisorRole}
                        onChange={(e) => {
                          handleDropDownChange(e);
                        }}
                      >
                        <option value="">Select</option>
                        {inspectionAgency
                          ? inspectionAgency.map((inspect) => (
                            <option
                              key={inspect.wld_description}
                              value={inspect.wld_description}
                            >
                              {inspect.wld_description}
                            </option>
                          ))
                          : null}
                      </Form.Select>
                      <Form.Label htmlFor="inspectionBy">
                        Inspection By
                      </Form.Label>
                    </div>
                    <div className="required-text-selectWeldRepair">
                      {error.inspectionBy.status && (
                        <Form.Text className="text-danger">
                          {error.inspectionBy.message}
                        </Form.Text>
                      )}
                    </div>
                  </Form.Group>
                </Col>
                <Col lg={3} className="mb-2">
                  <Form.Group className="g-input">
                    <div className="d-grid align-items-center">
                      <Form.Control
                        id="drawingNumber"
                        name="drawingNumber"
                        type="text"
                        maxLength="50"
                        placeholder=" "
                        value={weldRepairObj.drawingNumber}
                        readOnly={currentrole !== SupervisorRole}
                        onChange={handleChange}
                        onBlur={(e) => {
                          let error = isAlphanumericAllSpecialCharacters(
                            e.target.value
                          );
                          setError((prevState) => ({
                            ...prevState,
                            drawingNumber: error,
                          }));
                        }}
                      />
                      <Form.Label htmlFor="drawingNumber">
                        Drawing Number
                      </Form.Label>
                      {error.drawingNumber.status && (
                        <Form.Text className="text-danger">
                          {error.drawingNumber.message}
                        </Form.Text>
                      )}
                    </div>
                  </Form.Group>
                </Col>
                <Col lg={3}>
                  <Form.Group className="g-input">
                    <div className="d-grid align-items-center">
                      <Form.Control
                        id="sheetNumber"
                        name="sheetNumber"
                        type="text"
                        maxLength="50"
                        readOnly={currentrole !== SupervisorRole}
                        placeholder=" "
                        value={weldRepairObj.sheetNumber}
                        onChange={handleChange}
                        onBlur={(e) => {
                          let error = isAlphanumericAllSpecialCharacters(
                            e.target.value
                          );
                          setError((prevState) => ({
                            ...prevState,
                            sheetNumber: error,
                          }));
                        }}
                      />
                      <Form.Label htmlFor="sheetNumber">
                        Sheet Number
                      </Form.Label>
                      {error.sheetNumber.status && (
                        <Form.Text className="text-danger">
                          {error.sheetNumber.message}
                        </Form.Text>
                      )}
                    </div>
                  </Form.Group>
                </Col>
                <Col lg={3}>
                  <Form.Group className="g-input">
                    <div className="d-grid align-items-center">
                      <Form.Control
                        id="revisionNumber"
                        name="revisionNumber"
                        type="text"
                        maxLength="50"
                        readOnly={currentrole !== SupervisorRole}
                        placeholder=" "
                        value={weldRepairObj.revisionNumber}
                        onChange={handleChange}
                        onBlur={(e) => {
                          let error = isAlphanumericAllSpecialCharacters(
                            e.target.value
                          );
                          if (currentrole === SupervisorRole) {
                            setError((prevState) => ({
                              ...prevState,
                              revisionNumber: error,
                            }));
                          } else {
                            setError((prevState) => ({
                              ...prevState,
                              revisionNumber: {},
                            }));
                          }
                        }}
                      />
                      <Form.Label htmlFor="revisionNumber">
                        Revision No.
                      </Form.Label>
                      {error.revisionNumber.status && (
                        <Form.Text className="text-danger">
                          {error.revisionNumber.message}
                        </Form.Text>
                      )}
                    </div>
                  </Form.Group>
                </Col>
                <Col lg={3}>
                  <Form.Group className="g-input">
                    <div className="d-grid align-items-center">
                      <Form.Control
                        id="orginatingDept"
                        name="orginatingDept"
                        type="text"
                        readOnly
                        placeholder=" "
                        value={weldRepairObj.orginatingDept}
                      />
                      <Form.Label htmlFor="orginatingDept">
                        Originating Dept
                      </Form.Label>
                    </div>
                  </Form.Group>
                </Col>
                <Col lg={3} className="mb-2">
                  <Form.Group className="g-select">
                    <div className="d-grid align-items-center">
                      <Form.Select
                        id="jointNumber"
                        name="jointNumber"
                        type="text"
                        placeholder=" "
                        value={weldRepairObj.jointNumbers}
                        disabled={
                          action === "Edit" ||
                          currentrole === SupervisorRole ||
                          currentrole === "NDE" ||
                          currentrole === "QCE"
                        }
                        onChange={(e) => {
                          handleDropDownChange(e);
                        }}
                      >
                        {action === "Edit" ? (
                          <option value={rowjointnumber}>
                            {rowjointnumber}
                          </option>
                        ) : (
                          <option value="">Select</option>
                        )}

                        {jointNumbers
                          ? jointNumbers.map((jointnum) => (
                            <option key={jointnum} value={jointnum}>
                              {jointnum}
                            </option>
                          ))
                          : null}
                      </Form.Select>
                      <Form.Label htmlFor="jointNumber">
                        Joint Number
                      </Form.Label>
                    </div>
                    <div className="required-text-selectWeldRepair">
                      {error.jointNumber.status && (
                        <Form.Text className="text-danger">
                          {error.jointNumber.message}
                        </Form.Text>
                      )}
                    </div>
                  </Form.Group>
                </Col>
                <Col lg={3}>
                  <Form.Group className="g-input">
                    <div className="d-grid align-items-center">
                      <Form.Control
                        id="spotNumber"
                        name="spotNumber"
                        type="text"
                        maxLength="50"
                        readOnly={currentrole !== SupervisorRole}
                        placeholder=" "
                        value={weldRepairObj.spotNumber}
                        onChange={handleChange}
                        onBlur={(e) => {
                          let error = isAlphanumericAllSpecialCharacters(
                            e.target.value
                          );
                          setError((prevState) => ({
                            ...prevState,
                            spotNumber: error,
                          }));
                        }}
                      />
                      <Form.Label htmlFor="spotNumber">Spot No.</Form.Label>
                    </div>
                    <div className="required-text-selectWeldRepair">
                      {error.spotNumber.status && (
                        <Form.Text className="text-danger">
                          {error.spotNumber.message}
                        </Form.Text>
                      )}
                    </div>
                  </Form.Group>
                </Col>
                <Col lg={3}>
                  <Form.Group className="g-input">
                    <div className="d-grid align-items-center">
                      <Form.Control
                        id="repairRevision"
                        name="repairRevision"
                        type="text"
                        maxLength="5"
                        readOnly={currentrole !== SupervisorRole}
                        placeholder=" "
                        value={weldRepairObj.repairRevision}
                        onChange={handleChange}
                        onBlur={(e) => {
                          let error = isAlphanumericAllSpecialCharacters(
                            e.target.value
                          );
                          setError((prevState) => ({
                            ...prevState,
                            repairRevision: error,
                          }));
                        }}
                      />
                      <Form.Label htmlFor="repairRevision">
                        Repair Revision
                      </Form.Label>
                    </div>
                    <div className="required-text-selectWeldRepair">
                      {error.repairRevision.status && (
                        <Form.Text className="text-danger">
                          {error.repairRevision.message}
                        </Form.Text>
                      )}
                    </div>
                  </Form.Group>
                </Col>
                <Col lg={3}>
                  <Form.Group className="g-select">
                    <div className="d-grid align-items-center">
                      <Form.Select
                        id="ndtMethod"
                        name="ndtMethod"
                        type="text"
                        placeholder=" "
                        value={weldRepairObj.ndtMethod}
                        disabled={currentrole !== SupervisorRole}
                        onChange={(e) => {
                          handleDropDownChange(e);
                        }}
                      >
                        {NDT_Method.map((item) => {
                          return (
                            <option key={item.value} value={item.value}>{item.name}</option>
                          )
                        })}
                      </Form.Select>
                      <Form.Label htmlFor="ndtMethod">NDT Method</Form.Label>
                    </div>
                    <div className="required-text-selectWeldRepair">
                      {error.ndtMethod.status && (
                        <Form.Text className="text-danger">
                          {error.ndtMethod.message}
                        </Form.Text>
                      )}
                    </div>
                  </Form.Group>
                </Col>
                <Col lg={3}>
                  <Form.Group className="g-input">
                    <div className="d-grid align-items-center">
                      <Form.Control
                        id="material"
                        name="material"
                        type="text"
                        maxLength="50"
                        placeholder=" "
                        value={weldRepairObj.material}
                        readOnly={currentrole !== SupervisorRole}
                        onChange={handleChange}
                      />
                      <Form.Label htmlFor="material">Material</Form.Label>
                    </div>
                    <div className="required-text-selectWeldRepair">
                      {error.material.status && (
                        <Form.Text className="text-danger">
                          {error.material.message}
                        </Form.Text>
                      )}
                    </div>
                  </Form.Group>
                </Col>
                {currentrole === SupervisorRole ? (
                  <Col lg={3}>
                    <Form.Group className="g-select">
                      <Select
                        className="repairSelectDD"
                        id="weldingProcess"
                        name="weldingProcess"
                        options={weldingProcessResult}
                        isMulti
                        isDisabled={currentrole !== SupervisorRole}
                        closeMenuOnSelect={false}
                        hideSelectedOptions={true}
                        onChange={(e) => handleDropDownProcess(e)}
                        // value={selectedOptions}
                        value={getSelectValue()}
                        placeholder="Welding Process"
                      />
                      <div className="required-text-selectWeldRepair">
                        {error.weldingProcess.status && (
                          <Form.Text className="text-danger">
                            {error.weldingProcess.message}
                          </Form.Text>
                        )}
                      </div>
                    </Form.Group>
                  </Col>
                ) : (
                  <Col lg={3}>
                    <Form.Group className="g-input">
                      <div className="d-grid align-items-center">
                        <Form.Control
                          id="weldingProcess"
                          name="weldingProcess"
                          type="text"
                          maxLength="50"
                          placeholder=" "
                          value={weldRepairObj.weldingProcess}
                          readOnly={currentrole !== SupervisorRole}
                          onChange={handleChange}
                        />
                        <Form.Label htmlFor="Welding Process">
                          Welding Process
                        </Form.Label>
                      </div>
                      <div className="required-text-selectWeldRepair">
                        {error.weldingProcess.status && (
                          <Form.Text className="text-danger">
                            {error.weldingProcess.message}
                          </Form.Text>
                        )}
                      </div>
                    </Form.Group>
                  </Col>
                )}

                {currentrole === SupervisorRole ? (
                  <Col lg={3}>
                    <Form.Group className="g-input">
                      <Form.Control
                        id="welderNumber"
                        name="welderNumber"
                        type="text"
                        maxLength="50"
                        placeholder=" "
                        value={weldRepairObj.welderNumber}
                        disabled={currentrole !== SupervisorRole}
                      ></Form.Control>
                      <Form.Label
                        htmlFor="welderNumber"
                        style={{ background: "none" }}
                      >
                        Welder No.
                      </Form.Label>
                      {action === "Edit" && currentrole === SupervisorRole && (
                        <BiSelectMultiple
                          size={20}
                          onClick={() => {
                            showPopUp(
                              "Welding Repair- WI Welding Numbers",
                              "Multiple",
                              "Project Number",
                              weldRepairObj
                            );
                          }}
                          color={"gray"}
                          style={{ marginLeft: "13rem", marginTop: "-4.3rem" }}
                        />
                      )}
                      <div
                        className="required-text-selectWeldRepair"
                        style={{ position: "relative", bottom: "20px" }}
                      >
                        {error.welderNumber.status && (
                          <Form.Text className="text-danger">
                            {error.welderNumber.message}
                          </Form.Text>
                        )}
                      </div>
                    </Form.Group>
                  </Col>
                ) : (
                  <Col lg={3}>
                    <Form.Group className="g-input">
                      <div className="d-grid align-items-center">
                        <Form.Control
                          id="welderNumber"
                          name="welderNumber"
                          type="text"
                          maxLength="50"
                          placeholder=" "
                          value={weldRepairObj.welderNumber}
                          readOnly={currentrole !== SupervisorRole}
                          onChange={handleChange}
                        />
                        <Form.Label htmlFor="Welding Process">
                          Welder No.
                        </Form.Label>
                      </div>
                      <div className="required-text-selectWeldRepair">
                        {error.welderNumber.status && (
                          <Form.Text className="text-danger">
                            {error.welderNumber.message}
                          </Form.Text>
                        )}
                      </div>
                    </Form.Group>
                  </Col>
                )}

                <Col lg={3}>
                  <Form.Group className="g-input">
                    <div className="d-grid align-items-center">
                      <Form.Control
                        id="welderName"
                        name="welderName"
                        type="text"
                        maxLength="50"
                        placeholder=" "
                        value={weldRepairObj.welderName}
                        readOnly
                      />
                      <Form.Label htmlFor="welderName">Welder Name</Form.Label>
                    </div>
                  </Form.Group>
                </Col>
              </Row>
            </div>
          </div>
          <div className="weldRepairSecondDiv">
            <Row>
              <span
                className="WPS-blue-text"
                style={{ position: "relative", top: "10px" }}
              >
                Detail of Repair Removal
              </span>
              <Col lg={3}>
                <Form.Group className="g-input">
                  <div className="d-grid align-items-center">
                    <Form.Control
                      id="repairOrDefectType"
                      name="repairOrDefectType"
                      type="text"
                      maxLength="50"
                      placeholder=" "
                      value={weldRepairObj.repairOrDefectType}
                      readOnly={currentrole !== SupervisorRole}
                      onChange={handleChange}
                      onBlur={(e) => {
                        let error = isAlphanumericAllSpecialCharacters(
                          e.target.value
                        );
                        setError((prevState) => ({
                          ...prevState,
                          repairOrDefectType: error,
                        }));
                      }}
                    />
                    <Form.Label htmlFor="repairOrDefectType">
                      Repair Type/ Type of Defect
                    </Form.Label>
                  </div>
                  <div className="required-text-selectWeldRepair">
                    {error.repairOrDefectType.status && (
                      <Form.Text className="text-danger">
                        {error.repairOrDefectType.message}
                      </Form.Text>
                    )}
                  </div>
                </Form.Group>
              </Col>
              <Col lg={3}>
                <Form.Group className="g-select">
                  <div className="d-grid align-items-center">
                    <Form.Select
                      id="repairLocationMarkedBy"
                      name="repairLocationMarkedBy"
                      type="text"
                      placeholder=" "
                      value={weldRepairObj.repairLocationMarkedBy}
                      disabled={currentrole !== SupervisorRole}
                      onChange={(e) => {
                        handleDropDownChange(e);
                      }}
                    >
                      {
                        RepairLocationMarkedBy.map((item) => {
                          return (
                            <option key={item.value} value={item.value}>{item.name}</option>
                          )
                        })
                      }
                    </Form.Select>
                    <Form.Label htmlFor="repairLocationMarkedBy">
                      Repair Location Marked By
                    </Form.Label>
                  </div>
                  <div className="required-text-selectWeldRepair">
                    {error.repairLocationMarkedBy.status && (
                      <Form.Text className="text-danger">
                        {error.repairLocationMarkedBy.message}
                      </Form.Text>
                    )}
                  </div>
                </Form.Group>
              </Col>
              <Col lg={3}>
                <Form.Group className="g-input">
                  <div className="d-grid align-items-center">
                    <Form.Control
                      id="repairLengthSpotwise"
                      name="repairLengthSpotwise"
                      type="text"
                      maxLength="50"
                      placeholder=" "
                      value={weldRepairObj.repairLengthSpotwise}
                      readOnly={currentrole !== SupervisorRole}
                      onChange={handleChange}
                      onBlur={(e) => {
                        let error = isExperience(e.target.value);
                        setError((prevState) => ({
                          ...prevState,
                          repairLengthSpotwise: error,
                        }));
                      }}
                    />
                    <Form.Label htmlFor="repairLengthSpotwise">
                      Repair Length(Spotwise) in mm
                    </Form.Label>
                  </div>
                  <div className="required-text-selectWeldRepair">
                    {error.repairLengthSpotwise.status && (
                      <Form.Text className="text-danger">
                        {error.repairLengthSpotwise.message}
                      </Form.Text>
                    )}
                  </div>
                </Form.Group>
              </Col>
              <Col lg={3}>
                <Form.Group className="g-input">
                  <div className="d-grid align-items-center">
                    <Form.Control
                      id="repairLengthActualwise"
                      name="repairLengthActualwise"
                      type="text"
                      maxLength="50"
                      placeholder=" "
                      value={weldRepairObj.repairLengthActualwise}
                      readOnly={currentrole !== SupervisorRole}
                      onChange={handleChange}
                      onBlur={(e) => {
                        let error = isExperience(e.target.value);
                        setError((prevState) => ({
                          ...prevState,
                          repairLengthActualwise: error,
                        }));
                      }}
                    />
                    <Form.Label htmlFor="repairLengthActualwise">
                      Repair Length(Actual size) in mm
                    </Form.Label>
                  </div>
                  <div className="required-text-selectWeldRepair">
                    {error.repairLengthActualwise.status && (
                      <Form.Text className="text-danger">
                        {error.repairLengthActualwise.message}
                      </Form.Text>
                    )}
                  </div>
                </Form.Group>
              </Col>
            </Row>
          </div>
          <div className="weldRepairFirstDiv">
            <div className="weldRepairSecondDiv">
              <Row>
                <span
                  className="WPS-blue-text"
                  style={{ position: "relative", top: "10px" }}
                >
                  After Repair Removal
                </span>
                <Col lg={3}>
                  <Form.Group className="g-select">
                    <div className="d-grid align-items-center">
                      <Form.Select
                        id="repairRemovedByAfterRepairRemoval"
                        name="repairRemovedByAfterRepairRemoval"
                        type="text"
                        placeholder=" "
                        value={weldRepairObj.repairRemovedByAfterRepairRemoval}
                        disabled={currentrole !== SupervisorRole}
                        onChange={(e) => {
                          handleDropDownChange(e);
                        }}
                      >
                        {
                          RepairRemovedByAfterRepairRemoval.map((item) => {
                            return (
                              <option key={item.value} value={item.value}>{item.name}</option>
                            )
                          })
                        }
                      </Form.Select>
                      <Form.Label htmlFor="repairRemovedByAfterRepairRemoval">
                        Repair Removed By
                      </Form.Label>
                    </div>
                    <div className="required-text-selectWeldRepair">
                      {error.repairRemovedByAfterRepairRemoval.status && (
                        <Form.Text className="text-danger">
                          {error.repairRemovedByAfterRepairRemoval.message}
                        </Form.Text>
                      )}
                    </div>
                  </Form.Group>
                </Col>
                <Col lg={3}>
                  <Form.Group className="g-select">
                    <div className="d-grid align-items-center">
                      <Form.Select
                        id="repairFoundAfterRepairRemoval"
                        name="repairFoundAfterRepairRemoval"
                        type="text"
                        placeholder=" "
                        value={weldRepairObj.repairFoundAfterRepairRemoval}
                        disabled={
                          currentrole === "Initiator" ||
                          currentrole !== SupervisorRole
                        }
                        onChange={(e) => {
                          handleDropDownChange(e);
                        }}
                      >
                        {RepairFound.map((item) => {
                          return (
                            <option key={item.value} value={item.value}>
                              {item.name}
                            </option>
                          )
                        })}
                      </Form.Select>
                      <Form.Label htmlFor="repairFoundAfterRepairRemoval">
                        Repair Found
                      </Form.Label>
                    </div>
                    <div className="required-text-selectWeldRepair">
                      {error.repairFoundAfterRepairRemoval.status && (
                        <Form.Text className="text-danger">
                          {error.repairFoundAfterRepairRemoval.message}
                        </Form.Text>
                      )}
                    </div>
                  </Form.Group>
                </Col>
                <Col lg={3}>
                  <Form.Group className="g-input">
                    <div className="d-grid align-items-center">
                      <Form.Control
                        id="repairDepthAfterRepairRemoval"
                        name="repairDepthAfterRepairRemoval"
                        type="text"
                        maxLength="50"
                        placeholder=" "
                        value={weldRepairObj.repairDepthAfterRepairRemoval}
                        readOnly={currentrole !== SupervisorRole}
                        onChange={handleChange}
                        onBlur={(e) => {
                          let error = isExperience(e.target.value);
                          setError((prevState) => ({
                            ...prevState,
                            repairDepthAfterRepairRemoval: error,
                          }));
                        }}
                      />
                      <Form.Label htmlFor="repairDepthAfterRepairRemoval">
                        Repair Depth in mm
                      </Form.Label>
                    </div>
                    <div className="required-text-selectWeldRepair">
                      {error.repairDepthAfterRepairRemoval.status && (
                        <Form.Text className="text-danger">
                          {error.repairDepthAfterRepairRemoval.message}
                        </Form.Text>
                      )}
                    </div>
                  </Form.Group>
                </Col>
                <Col lg={3}>
                  <Form.Group className="g-input">
                    <div className="d-grid align-items-center">
                      <Form.Control
                        id="defectiveProcessAfterRepairRemoval"
                        name="defectiveProcessAfterRepairRemoval"
                        type="text"
                        maxLength="100"
                        placeholder=" "
                        value={weldRepairObj.defectiveProcessAfterRepairRemoval}
                        // readOnly={currentrole !== SupervisorRole}
                        readOnly
                        onChange={handleChange}
                      />
                      <Form.Label htmlFor="defectiveProcessAfterRepairRemoval">
                        Defective Process
                      </Form.Label>
                    </div>
                    <div className="required-text-selectWeldRepair">
                      {error.defectiveProcessAfterRepairRemoval.status && (
                        <Form.Text className="text-danger">
                          {error.defectiveProcessAfterRepairRemoval.message}
                        </Form.Text>
                      )}
                    </div>
                  </Form.Group>
                </Col>
                <Col lg={12}>
                  <Form.Group className="g-input">
                    <div className="d-grid align-items-center">
                      <Form.Control
                        id="weldingJointDetailAfterRepairRemoval"
                        name="weldingJointDetailAfterRepairRemoval"
                        type="text"
                        maxLength="50"
                        placeholder=" "
                        value={
                          weldRepairObj.weldingJointDetailAfterRepairRemoval
                        }
                        readOnly={currentrole !== SupervisorRole}
                        onChange={handleChange}
                        onBlur={(e) => {
                          let error = isAlphanumericAllSpecialCharacters(
                            e.target.value
                          );
                          setError((prevState) => ({
                            ...prevState,
                            weldingJointDetailAfterRepairRemoval: error,
                          }));
                        }}
                      />
                      <Form.Label htmlFor="weldingJointDetailAfterRepairRemoval">
                        Welding Joint Detail
                      </Form.Label>
                    </div>
                    <div className="required-text-selectWeldRepair">
                      {error.weldingJointDetailAfterRepairRemoval.status && (
                        <Form.Text className="text-danger">
                          {error.weldingJointDetailAfterRepairRemoval.message}
                        </Form.Text>
                      )}
                    </div>
                  </Form.Group>
                </Col>
              </Row>
            </div>
          </div>
          <div className="weldRepairSecondDiv">
            <Row>
              <span
                className="WPS-blue-text"
                style={{ position: "relative", top: "10px" }}
              >
                Root Cause Analysis
              </span>
              <Col md={12}>
                <div className="T-input w-100">
                  <Form.Control
                    id="rootCauseAnalysisWhy1"
                    name="rootCauseAnalysisWhy1"
                    style={{ height: "100px" }}
                    type="text"
                    as="textarea"
                    rows={3}
                    maxLength={500}
                    placeholder=" "
                    value={weldRepairObj.rootCauseAnalysisWhy1}
                    readOnly={currentrole !== SupervisorRole}
                    onChange={handleChange}
                    onBlur={(e) => {
                      let error = isAlphanumericAllSpecialCharacters(
                        e.target.value
                      );
                      setError((prevState) => ({
                        ...prevState,
                        rootCauseAnalysisWhy1: error,
                      }));
                    }}
                  />
                  <Form.Label htmlFor="rootCauseAnalysisWhy1">Why-1</Form.Label>
                </div>
                <div
                  className="required-text-selectWeldRepair"
                  style={{ position: "relative", bottom: "8px" }}
                >
                  {error.rootCauseAnalysisWhy1.status && (
                    <Form.Text className="text-danger">
                      {error.rootCauseAnalysisWhy1.message}
                    </Form.Text>
                  )}
                </div>
              </Col>
              <Col md={12}>
                <div className="T-input w-100">
                  <Form.Control
                    id="rootCauseAnalysisWhy2"
                    name="rootCauseAnalysisWhy2"
                    type="text"
                    as="textarea"
                    rows={3}
                    style={{ height: "100px" }}
                    maxLength={500}
                    placeholder=" "
                    value={weldRepairObj.rootCauseAnalysisWhy2}
                    readOnly={currentrole !== SupervisorRole}
                    onChange={handleChange}
                    onBlur={(e) => {
                      let error = isAlphanumericAllSpecialCharacters(
                        e.target.value
                      );
                      setError((prevState) => ({
                        ...prevState,
                        rootCauseAnalysisWhy2: error,
                      }));
                    }}
                  />
                  <Form.Label htmlFor="rootCauseAnalysisWhy2">Why-2</Form.Label>
                </div>
              </Col>
              <Col md={12}>
                <div className="T-input w-100">
                  <Form.Control
                    id="rootCauseAnalysisWhy3"
                    name="rootCauseAnalysisWhy3"
                    style={{ height: "100px" }}
                    type="text"
                    as="textarea"
                    rows={3}
                    maxLength={500}
                    placeholder=" "
                    value={weldRepairObj.rootCauseAnalysisWhy3}
                    readOnly={currentrole !== SupervisorRole}
                    onChange={handleChange}
                    onBlur={(e) => {
                      let error = isAlphanumericAllSpecialCharacters(
                        e.target.value
                      );
                      setError((prevState) => ({
                        ...prevState,
                        WPQ_Page2_Notes: error,
                      }));
                    }}
                  />
                  <Form.Label htmlFor="rootCauseAnalysisWhy3">Why-3</Form.Label>
                </div>
              </Col>
              <Col md={12}>
                <div className="T-input w-100">
                  <Form.Control
                    id="rootCauseAnalysisWhy4"
                    name="rootCauseAnalysisWhy4"
                    style={{ height: "100px" }}
                    type="text"
                    as="textarea"
                    rows={3}
                    maxLength={500}
                    placeholder=" "
                    value={weldRepairObj.rootCauseAnalysisWhy4}
                    readOnly={currentrole !== SupervisorRole}
                    onChange={handleChange}
                    onBlur={(e) => {
                      let error = isAlphanumericAllSpecialCharacters(
                        e.target.value
                      );
                      setError((prevState) => ({
                        ...prevState,
                        rootCauseAnalysisWhy4: error,
                      }));
                    }}
                  />
                  <Form.Label htmlFor="rootCauseAnalysisWhy4">Why-4</Form.Label>
                </div>
              </Col>
              <Col md={12}>
                <div className="T-input w-100">
                  <Form.Control
                    id="rootCauseAnalysisWhy5"
                    name="rootCauseAnalysisWhy5"
                    style={{ height: "100px" }}
                    type="text"
                    as="textarea"
                    rows={3}
                    maxLength={500}
                    placeholder=" "
                    value={weldRepairObj.rootCauseAnalysisWhy5}
                    readOnly={currentrole !== SupervisorRole}
                    onChange={handleChange}
                    onBlur={(e) => {
                      let error = isAlphanumericAllSpecialCharacters(
                        e.target.value
                      );
                      setError((prevState) => ({
                        ...prevState,
                        rootCauseAnalysisWhy5: error,
                      }));
                    }}
                  />
                  <Form.Label htmlFor="rootCauseAnalysisWhy5">Why-5</Form.Label>
                </div>
              </Col>
            </Row>
          </div>
          <div className="weldRepairFirstDiv">
            <div className="weldRepairSecondDiv">
              <Row>
                <Col md={12}>
                  <div className="T-input w-100">
                    <Form.Control
                      id="rootCauseAnalysisOthers"
                      name="rootCauseAnalysisOthers"
                      type="text"
                      as="textarea"
                      rows={3}
                      style={{ height: "100px" }}
                      maxLength={500}
                      placeholder=" "
                      value={weldRepairObj.rootCauseAnalysisOthers}
                      readOnly={currentrole !== SupervisorRole}
                      onChange={handleChange}
                      onBlur={(e) => {
                        let error = isAlphanumericAllSpecialCharacters(
                          e.target.value
                        );
                        setError((prevState) => ({
                          ...prevState,
                          rootCauseAnalysisOthers: error,
                        }));
                      }}
                    />
                    <Form.Label htmlFor="rootCauseAnalysisOthers">
                      Others
                    </Form.Label>
                  </div>
                </Col>
                <Col md={12}>
                  <div className="T-input w-100">
                    <Form.Control
                      id="rootCauseAnalysisRootCause"
                      name="rootCauseAnalysisRootCause"
                      type="text"
                      as={"textarea"}
                      rows={3}
                      style={{ height: "100px" }}
                      maxLength={500}
                      placeholder=" "
                      value={weldRepairObj.rootCauseAnalysisRootCause}
                      readOnly={currentrole !== SupervisorRole}
                      onChange={handleChange}
                      onBlur={(e) => {
                        let error = isAlphanumericAllSpecialCharacters(
                          e.target.value
                        );
                        setError((prevState) => ({
                          ...prevState,
                          rootCauseAnalysisOthers: error,
                        }));
                      }}
                    />
                    <Form.Label htmlFor="rootCauseAnalysisRootCause">
                      Root Cause
                    </Form.Label>
                  </div>
                </Col>
                <Col md={12}>
                  <div className="T-input w-100">
                    <Form.Control
                      id="rootCauseAnalysisCorrectiveAction"
                      name="rootCauseAnalysisCorrectiveAction"
                      type="text"
                      as={"textarea"}
                      rows={3}
                      style={{ height: "100px" }}
                      maxLength={500}
                      placeholder=" "
                      value={weldRepairObj.rootCauseAnalysisCorrectiveAction}
                      readOnly={currentrole !== SupervisorRole}
                      onChange={handleChange}
                      onBlur={(e) => {
                        let error = isAlphanumericAllSpecialCharacters(
                          e.target.value
                        );
                        setError((prevState) => ({
                          ...prevState,
                          rootCauseAnalysisOthers: error,
                        }));
                      }}
                    />
                    <Form.Label htmlFor="rootCauseAnalysisCorrectiveAction">
                      Corrective Action
                    </Form.Label>
                  </div>
                </Col>
                <Col md={12}>
                  <div className="T-input w-100">
                    <Form.Control
                      id="rootCauseAnalysisPreventionAction"
                      name="rootCauseAnalysisPreventionAction"
                      type="text"
                      as={"textarea"}
                      rows={3}
                      style={{ height: "100px" }}
                      maxLength={500}
                      placeholder=" "
                      value={weldRepairObj.rootCauseAnalysisPreventionAction}
                      readOnly={currentrole !== SupervisorRole}
                      onChange={handleChange}
                      onBlur={(e) => {
                        let error = isAlphanumericAllSpecialCharacters(
                          e.target.value
                        );
                        setError((prevState) => ({
                          ...prevState,
                          rootCauseAnalysisOthers: error,
                        }));
                      }}
                    />
                    <Form.Label htmlFor="rootCauseAnalysisPreventionAction">
                      Preventive Action
                    </Form.Label>
                  </div>
                </Col>
                <Col md={12}>
                  <div className="T-input w-100">
                    <Form.Control
                      id="rootCauseAnalysisKaizenIdea"
                      name="rootCauseAnalysisKaizenIdea"
                      type="text"
                      as={"textarea"}
                      rows={3}
                      style={{ height: "100px" }}
                      maxLength={500}
                      placeholder=" "
                      value={weldRepairObj.rootCauseAnalysisKaizenIdea}
                      readOnly={currentrole !== SupervisorRole}
                      onChange={handleChange}
                      onBlur={(e) => {
                        let error = isAlphanumericAllSpecialCharacters(
                          e.target.value
                        );
                        setError((prevState) => ({
                          ...prevState,
                          rootCauseAnalysisOthers: error,
                        }));
                      }}
                    />
                    <Form.Label htmlFor="rootCauseAnalysisKaizenIdea">
                      Kaizen Idea
                    </Form.Label>
                  </div>
                </Col>
                <Col md={12}>
                  <div className="T-input w-100">
                    <Form.Control
                      name="rootCauseAnalysisCounterMeasure"
                      id="rootCauseAnalysisCounterMeasure"
                      type="text"
                      as={"textarea"}
                      rows={3}
                      style={{ height: "100px" }}
                      maxLength={500}
                      placeholder=" "
                      value={weldRepairObj.rootCauseAnalysisCounterMeasure}
                      readOnly={currentrole !== SupervisorRole}
                      onChange={handleChange}
                      onBlur={(e) => {
                        let error = isAlphanumericAllSpecialCharacters(
                          e.target.value
                        );
                        setError((prevState) => ({
                          ...prevState,
                          rootCauseAnalysisCounterMeasure: error,
                        }));
                      }}
                    />
                    <Form.Label htmlFor="rootCauseAnalysisCounterMeasure">
                      Counter Measure
                    </Form.Label>
                  </div>
                </Col>
                <Col md={12}>
                  <div className="T-input w-100">
                    <Form.Control
                      name="rootCauseAnalysisCostOfRepair"
                      id="rootCauseAnalysisCostOfRepair"
                      type="text"
                      as={"textarea"}
                      rows={3}
                      style={{ height: "100px" }}
                      maxLength={500}
                      placeholder=" "
                      value={weldRepairObj.rootCauseAnalysisCostOfRepair}
                      readOnly={currentrole !== SupervisorRole}
                      onChange={handleChange}
                      onBlur={(e) => {
                        let error = isAlphanumericAllSpecialCharacters(
                          e.target.value
                        );
                        setError((prevState) => ({
                          ...prevState,
                          rootCauseAnalysisCostOfRepair: error,
                        }));
                      }}
                    />
                    <Form.Label htmlFor="rootCauseAnalysisCostOfRepair">
                      Cost of repair
                    </Form.Label>
                  </div>
                </Col>
              </Row>
            </div>
          </div>
          {(action === "Edit" || action === "View") && (
            <div
              id="closureRem"
              name="closureRem"
              className="weldRepairSecondDiv"
            >
              <Row>
                <Form.Label htmlFor="closureRem">Closure Remarks </Form.Label>
                <ClosureRemarksWeldingRepair
                  rowData={weldRepairObj}
                  ExperienceNewRev={weldRepairObj.reportNumber}
                  callReload={callReload}
                  setMainReload={props.setMainReload}
                  remarksData={(e, closureparams, weldingparams, levelname) =>
                    getRemarks(e, closureparams, weldingparams, levelname)
                  }
                  errorData={error}
                  action={action}
                />
              </Row>
            </div>
          )}
        </div>
        <div className="weldRepairSecondDiv">
          <Row>
            <Col lg={6}>
              <Button
                variant="outline-success"
                type="button"
                className="me-2"
                onClick={() => {
                  handlecancel();
                }}
              >
                Cancel
              </Button>
            </Col>
            <Col lg={3}></Col>
            <Col lg={3}>
              <span>
                {action === "Edit" && currentrole === SupervisorRole && (
                  <Button
                    name="tempsave"
                    id="tempsave"
                    variant="outline-success"
                    type="submit"
                    className="me-2"
                  >
                    Temp Save
                  </Button>
                )}
                <Button type="submit" className="btn-add-color float-end  me-2">
                  Save
                </Button>
              </span>
            </Col>
          </Row>
        </div>
        <RenderIf isTrue={popUp.type === "Multiple" && showForm}>
          <MultiValuePopUp
            show={showForm}
            onHide={() => setShowForm(false)}
            setSelected={setPopUpData}
            masterName={popUp.name}
            paramkey={popUp.key}
            paramvalue={popUp.value}
            backdrop="static"
          />
        </RenderIf>
      </Form>
    </>
  );
});

export default WeldingRepairAnalysisEntry;
